import React from 'react';
import classNames from 'classnames';

interface EmailFieldProps {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  autoComplete?: string;
  className?: string;
  helpText?: string;
}

export const EmailField: React.FC<EmailFieldProps> = ({
  id,
  name,
  label,
  value,
  onChange,
  onBlur,
  placeholder = 'e.g., john.smith@company.com',
  error,
  required = false,
  disabled = false,
  autoComplete = 'email',
  className,
  helpText,
}) => {
  const hasError = Boolean(error);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    onChange(rawValue);
  };

  const handleBlur = () => {
    // Trim whitespace on blur
    if (value !== value.trim()) {
      onChange(value.trim());
    }
    if (onBlur) {
      onBlur();
    }
  };

  return (
    <div className={classNames('space-y-1', className)}>
      <label
        htmlFor={id}
        className={classNames(
          'block text-sm font-medium',
          hasError ? 'text-error-700' : 'text-gray-700'
        )}
      >
        {label}
        {required && <span className="text-error-600 ml-1">*</span>}
      </label>
      
      <div className="relative">
        <input
          type="email"
          id={id}
          name={name}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          className={classNames(
            'form-input',
            {
              'border-error-300 focus:border-error-500 focus:ring-error-500': hasError,
              'opacity-50 cursor-not-allowed': disabled,
            }
          )}
          aria-invalid={hasError}
          aria-describedby={helpText ? `${id}-help` : undefined}
        />
        
        {/* Email icon */}
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
      </div>

      {/* Help text and validation feedback */}
      <div className="mt-1 space-y-1">
        {helpText && (
          <p id={`${id}-help`} className="text-sm text-gray-500">
            {helpText}
          </p>
        )}
        
        {error && (
          <div className="validation-error">
            <div className="flex items-center">
              <svg className="h-4 w-4 text-error-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">{error}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
